<template>
    <div class="teamschedule-list">
        <span class="facet-label">{{ facetName }}</span>
        <div
            class="multi-select-wrapper"
            @focusout="resetSearch"
        >
            <al-multi-select
                ref="multiSelectRef"
                :value="selected"
                size="large"
                style="--size-control-default:36px; width: 100%;"
                class="multi-select"
                placeholder="Select teams"
                variant="wfm"
                test-id="workstreamSelect"
                :search="searchText"
                @change="emitSelected($event)"
                :key="groupedOptions.length"
            >
                <!-- eslint-disable -->
        <div v-if="selected.length === 0" slot="label" test-id="workstreamSelect" class="facet-label">None selected</div>
        <!-- eslint-enable -->

                <template
                    v-for="[groupName, members] in groupedOptions"
                    :key="groupName"
                >
                    <al-select-option-group>
                        <!-- eslint-disable -->
            <div slot="label">{{ groupName }}</div>
            <!-- eslint-enable -->

                        <al-select-option
                            v-for="member in members"
                            :key="member.id"
                            :value="member.id"
                            :display-value="member.label"
                            :groupvalue="groupName"
                        >
                            {{ member.label }}
                        </al-select-option>
                    </al-select-option-group>
                </template>
            </al-multi-select>
        </div>
    </div>
</template>

<script setup lang="ts">
import {
    ref, computed, inject, onMounted, watch
} from 'vue';
import { useSelectedViewFacet } from '@/components/TeamAndSchedule/composables/useSelectedViewFacet';
import { FacetItem } from '@/components/TeamAndSchedule/types/facetsTypes';
import type { InjectedPagePreferences } from '@agyletime/vue-3-common/src/typings/pagePreference';
import { loadFacetData } from '@/components/TeamAndSchedule/composables/useFacetSessionLoader';
import '@playvox/almendra/dist/components/icon';
import '@playvox/almendra/dist/components/checkbox';
import '@playvox/almendra/dist/components/input';

const multiSelectRef = ref<{ numberOfVisibleOptions?: number; numberOfOptions?: number; searchText?: string } | null>(null);
const pagePreferences = inject<InjectedPagePreferences>('pagePreferences');
const resetFilters = inject('resetFilters', ref(false));

interface TeamScheduleOption {
    label: string;
    id: number | string;
    data?: Record<string, unknown>; // replaced any
}

type GroupedTeamScheduleOptions = Record<string, TeamScheduleOption[]>;

const props = defineProps<{
  options: GroupedTeamScheduleOptions; // 🆕 grouped data directly from backend
  facetId: string;
  facetName: string;
  urlKey?: string;
  isLoading: boolean;
  activeTable: string;
  labelMap?: Record<string, string>;
}>();

const emit = defineEmits(['change']);

const selected = ref('');
const searchText = ref('');

// --- Convert grouped object to iterable entries ---
const groupedOptions = computed(() => Object.entries(props.options || {}));

// --- Restore selected values from sessionStorage first, URL as fallback ---
const loadSelectedValuesFromUrl = () => {
    const opts = props.options;
    if (!opts || Object.keys(opts).length === 0) return;

    const facetData = loadFacetData<string>(props.activeTable, props.facetId);
    selected.value = facetData?.value || '';
};

onMounted(() => {
    //  loadSelectedValuesFromUrl();
});

function resetSearch() {
    // Almendra internal API – exists in v2.59.0
    if (multiSelectRef.value?.numberOfVisibleOptions || multiSelectRef.value?.numberOfVisibleOptions === 0) {
        multiSelectRef.value.numberOfVisibleOptions = multiSelectRef.value?.numberOfOptions;   // clears search + filtered items
    }
}

watch(() => props.options, (newOptions, oldOptions) => {
    if (newOptions && Object.keys(newOptions).length && newOptions !== oldOptions) {
        loadSelectedValuesFromUrl();
    }
}, { immediate: true });

// --- Handle selection change ---
const emitSelected = (event: Event & { detail?: { value?: TeamScheduleOption[] } }) => {
    // Support almendra custom event with detail + plain event target fallback
    const targetValue = (event as unknown as { target?: { value?: unknown } }).target?.value;
    const newValue = event.detail?.value || targetValue;
    selected.value = typeof newValue === 'string' ? newValue : '';

    emit('change', {
        value: selected.value,
        facetId: props.facetId,
        facetName: props.facetName,
        urlKey: props.urlKey
    });
};

const _selectedCount = computed(() => selected.value.length); // underscore to mark unused

// --- Sync with selected view facets ---
useSelectedViewFacet({
    pagePreferences,
    activeTable: props.activeTable,
    facetId: props.facetId,
    resetFilters,
    onSelectedViewFacetChange(value: unknown) {
        const selectedViewValue = value as FacetItem;
        if (sessionStorage.getItem('usersData')) {
            if (typeof selectedViewValue?.value === 'string') {
                selected.value = selectedViewValue.value;
            } else {
                selected.value = '';
            }
        }
    }
});
</script>

<style scoped>
.teamschedule-list {
  width: 100%;
  font-family: Roboto;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
  color: #1A2021;
}

.multi-select-wrapper {
  margin-top: 4px;
}

:deep(.multi-select) {
  padding-top: 0;
  font-family: Roboto !important;
}

:deep(.multi-select)::part(control) {
  border: 1px solid #DCE3E4 !important;
  font-family: Roboto !important;
  height: 28px !important;
}

:deep(.multi-select)::part(combobox) {
  font-family: Roboto !important;
}

:deep(.multi-select)::part(input) {
  font-family: Roboto !important;
}

.facet-label {
  font-family: Roboto;
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  line-height: 20px;
  letter-spacing: 0%;
  vertical-align: middle;
  color: #1A2021;
}

.al-select-option-group {
  margin-top: var(--spacing-1, 4px);
}

:deep(.multi-select)::part(label) {
  background: transparent !important;
}

:deep(.multi-select [slot="label"]),
:deep(.multi-select)::part(display-value),
:deep(.facet-label) {
  background: transparent !important;
  font-family: Roboto;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0;
  vertical-align: middle;
  color: #1A2021;
}

/* Style the dropdown chevron icon */
:deep(.multi-select-chevron),
:deep(.al-icon),
:deep([class*="chevron"]),
:deep([class*="icon-chevron"]) {
  font-family: playvox-icons;
  font-weight: 400;
  font-style: normal;
  font-size: 24px;
  line-height: 24px;
  letter-spacing: 0;
  vertical-align: middle;
  color: #1A2021;
}

/* Position the icon at the end with proper spacing */
.multi-select {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.multi-select [slot="label"] {
  flex: 1;
  margin-right: 24px;
}

/* Style dropdown options */
:deep(al-select-option),
:deep(.al-select-option),
:deep([role="option"]),
:deep(.option) {
  font-family: Roboto;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  letter-spacing: 0;
  vertical-align: middle;
  color: #1A2021;
}

/* Style option group labels */
:deep(al-select-option-group),
:deep(.al-select-option-group),
:deep([role="group"]) {
  font-family: Roboto;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  letter-spacing: 0;
  vertical-align: middle;
  color: #1A2021;
}
</style>
